import React, { useEffect, useState } from 'react';
import './Content.css';
import CustomMap from './ContentComponents/Map/Map';
import Sidebar from './ContentComponents/Sidebar/Sidebar';
import { ContentProps } from '../../types';
import { useFetchDataDetail } from '../../hooks/useFetchDataDetail';
import config from '../../config';
import Handlebars from 'handlebars';
import { Item } from '../../types';
import { APIProvider } from '@vis.gl/react-google-maps';

Handlebars.registerHelper(
  'ifCond',
  function (this: any, v1: any, v2: any, options: Handlebars.HelperOptions) {
    if (v1 === v2) {
      return options.fn(true);
    }
    return options.inverse(this);
  },
);

Handlebars.registerHelper(
  'getItem',
  function (data: Item[], propertyName: keyof Item, propertyValue: string) {
    const item: Item | undefined = data.find(
      (item) => item[propertyName] === propertyValue,
    );
    return item;
  },
);

Handlebars.registerHelper('getValueItem', function (item: Item, options: any) {
  const context = options.data.root;
  const measurementUnits = context.measurementUnits;

  if (measurementUnits !== 'IMP') {
    if (item.value_sint !== -9999) {
      return item.value_sint.toString();
    } else {
      return '-';
    }
  } else if (item.value_simp !== -9999) {
    return item.value_simp.toString();
  } else {
    return '-';
  }
});

Handlebars.registerHelper('getUnitItem', function (item: Item, options: any) {
  const context = options.data.root;
  const measurementUnits = context.measurementUnits;
  if (measurementUnits != 'IMP') {
    return item.units_sint.toString();
  } else {
    return item.units_simp.toString();
  }
});

const Content: React.FC<ContentProps> = ({ measurementUnits, theme }) => {
  const { apiUrl, apiKey, website: configWebsite } = config;

  const [isActive, setIsActive] = useState(false);
  const [noData, setNoData] = useState(true);
  const [selectedId, setSelectedId] = useState('0');
  const [selectedNameBuoy, setSelectedNameBuoy] = useState('');
  const {
    dataBuoy,
    lastTimeStamp,
    lastBuoyTimeStamp,
    getRefreshTime,
    logoBuoy,
    fetchLastTimeStamp,
  } = useFetchDataDetail(apiUrl, selectedId);
  const [htmlDetailBuoy, setHtmlDetailBuoy] = useState<string>('');
  const [timerRunning, setTimerRunning] = useState(false);
  const [buoysLoadMap, setBuoysLoadMap] = useState(false);
  const [utcOffset, setUtcOffset] = useState<number>(0);

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const handleToggle = (click: string) => {
    if (click === '') {
      setIsActive(!isActive);
    } else if (click === 'MAP') {
      setIsActive(false);
    } else {
      setIsActive(true);
    }
  };

  const callBuoy = (id: string, name: string, utc: number) => {
    setSelectedNameBuoy(name);
    setNoData(false);
    setIsActive(true);
    setSelectedId(id);
    setUtcOffset(utc);
  };

  useEffect(() => {
    setTimerRunning(false);
    if (getRefreshTime > 0) {
      setTimerRunning(true);
    }
  }, [getRefreshTime]);

  useEffect(() => {
    let intervalId: any;
    if (timerRunning) {
      intervalId = setInterval(() => {
        fetchLastTimeStamp();
      }, getRefreshTime * 1000);
    } else {
      clearInterval(intervalId);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [timerRunning]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (
      dataBuoy != null &&
      dataBuoy.length > 0 &&
      lastTimeStamp != '' &&
      lastBuoyTimeStamp != ''
    ) {
      const firstItem = dataBuoy[0];

      const fetchTemplate = async () => {
        try {
          const item: Item = firstItem;
          let templateResponse = await fetch(
            'assets/template/template_detail_buoy_id_' + item.id_buoy + '.hbs',
          );

          // Check if the response is OK
          if (!templateResponse.ok) {
            throw new Error(`HTTP error! status: ${templateResponse.status}`);
          }

          let templateString = await templateResponse.text();

          if (
            templateString.trim().length === 0 ||
            templateString.includes('404') ||
            templateString.includes('Not Found') ||
            templateString.includes('<!DOCTYPE html>')
          ) {
            console.warn(
              'SPECIFIC TEMPLATE NOT FOUND OR INVALID. FETCHING DEFAULT TEMPLATE.',
            );
            // Fetch default template
            templateResponse = await fetch(
              'assets/template/template_detail_buoy.hbs',
            );

            if (!templateResponse.ok) {
              throw new Error(
                `Default template fetch failed with status: ${templateResponse.status}`,
              );
            }

            templateString = await templateResponse.text();

            if (
              templateString.trim().length === 0 ||
              templateString.includes('404') ||
              templateString.includes('Not Found') ||
              templateString.includes('<!DOCTYPE html>')
            ) {
              console.error(
                'DEFAULT TEMPLATE NOT FOUND OR INVALID. Setting a fallback HTML content.',
              );
              setHtmlDetailBuoy(
                '<div><h2>Data Not Available</h2><p>Unable to load buoy details at this time.</p></div>',
              );
              return;
            }

            const template = Handlebars.compile(templateString);

            const latStr = dataBuoy.length > 0 ? dataBuoy[0].lat_str : 'N/A';
            const longiStr =
              dataBuoy.length > 0 ? dataBuoy[0].longi_str : 'N/A';

            const generatedContent = template({
              dataBuoy,
              firstItem,
              measurementUnits,
              lastTimeStamp,
              lastBuoyTimeStamp,
              selectedNameBuoy,
              logoBuoy,
              lat_str: latStr,
              longi_str: longiStr,
            });
            setHtmlDetailBuoy(generatedContent);
          } else {
            const template = Handlebars.compile(templateString);

            const latStr = dataBuoy.length > 0 ? dataBuoy[0].lat_str : 'N/A';
            const longiStr =
              dataBuoy.length > 0 ? dataBuoy[0].longi_str : 'N/A';

            const generatedContent = template({
              dataBuoy,
              firstItem,
              measurementUnits,
              lastTimeStamp,
              lastBuoyTimeStamp,
              selectedNameBuoy,
              logoBuoy,
              lat_str: latStr,
              longi_str: longiStr,
            });
            setHtmlDetailBuoy(generatedContent);
          }
        } catch (error) {
          console.error('ERROR FETCHING TEMPLATE:', error);
          setHtmlDetailBuoy(
            '<div><h2>Error Loading Data</h2><p>There was an issue fetching buoy details.</p></div>',
          );
        }
      };

      fetchTemplate();
    }
  }, [dataBuoy, lastTimeStamp, lastBuoyTimeStamp, logoBuoy, measurementUnits]);

  return (
    <div className="wrapper">
      <Sidebar
        onToggle={handleToggle}
        isActive={isActive}
        htmlDetailBuoy={htmlDetailBuoy}
        noData={noData}
        mobile={windowSize.width < 992}
        buoysLoadMap={buoysLoadMap}
        dataBuoy={dataBuoy}
        measurementUnits={measurementUnits}
        lastTimeStamp={lastTimeStamp}
        lastBuoyTimeStamp={lastBuoyTimeStamp}
        selectedNameBuoy={selectedNameBuoy}
        logoBuoy={logoBuoy}
        lat_str={''}
        longi_str={''}
        theme={theme}
        websiteType={configWebsite}
        utcOffset={utcOffset}
      />
      <div className={`map ${isActive ? 'map-close' : ''}`}>
        <APIProvider apiKey={apiKey}>
          <CustomMap callBuoy={callBuoy} setBuoysLoadMap={setBuoysLoadMap} />
        </APIProvider>
      </div>
    </div>
  );
};

export default Content;
